<template>
  <div id="performance-summary-report" class="page-container">
    <!-- Add these meta tags -->
    <meta name="theme-color" content="#6D27BF">
    <meta name="apple-mobile-web-app-status-bar-style" content="#6D27BF">

    <div class="page-content">
      <div class="report-container">
        <div class="header-bar"></div>
        <!-- Replace the commented loading section with this -->
        <div v-if="loading" class="loading-container">
          <div class="loading-content">
            <loading-even></loading-even>
            <p class="loading-message">
              Analyzing performance data and generating your summary...
            </p>
            <p class="loading-submessage">
              This may take a few seconds
            </p>
          </div>
        </div>
        <!-- Add v-else to only show content when not loading -->
        <div v-else class="content-wrapper">
          <h1 class="title">Your Growth Journey at {{ user.company_name }}</h1>
          <p class="subtitle">Performance Summary: {{ period }}</p>
          <div class="metrics-container">
            <div class="icon-container" style="flex: 1;">
              <div>
                <img :src="user.image" alt="profile icon">
              </div>
              <div class="icon-text">
                <span class="fs-18 fw-700">{{ user.name }}</span>
                <span class="fs-16">{{ user.team_name }}</span>
              </div>
            </div>
            <div class="metrics-grid">
              <div class="metric-item">
                <h2>Goals</h2>
                <div class="relative">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="30" fill="none" stroke-width="4" stroke="#E0E0E0"></circle>
                    <circle cx="32" cy="32" r="30" fill="none" :stroke-width="4"
                      :stroke="getStrokeColor(overall_value.goal_value)" :stroke-dasharray="188.5"
                      :stroke-dashoffset="188.5 - (overall_value.goal_value * 1.885)" transform="rotate(-90 32 32)">
                    </circle>
                  </svg>
                  <div class="score-label">
                    <div class="fw-700 main">{{ overall_value.goal_value }}</div>
                    <!-- <div class="fs-16 fw-500 description">Quality</div> -->
                  </div>
                </div>
              </div>
              <div class="metric-item">
                <h2>Values</h2>
                <div class="relative">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="30" fill="none" stroke-width="4" stroke="#E0E0E0"></circle>
                    <circle cx="32" cy="32" r="30" fill="none" :stroke-width="4"
                      :stroke="getStrokeColor(overall_value.culture_value)" :stroke-dasharray="188.5"
                      :stroke-dashoffset="188.5 - (overall_value.culture_value * 1.885)" transform="rotate(-90 32 32)">
                    </circle>
                  </svg>
                  <div class="score-label">
                    <div class="fw-700 main">{{ overall_value.culture_value }}</div>
                    <!-- <div class="fs-16 fw-500 description">Quality</div> -->
                  </div>
                </div>
              </div>
              <div class="metric-item">
                <h2>Overall</h2>
                <div class="relative">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="30" fill="none" stroke-width="4" stroke="#E0E0E0"></circle>
                    <circle cx="32" cy="32" r="30" fill="none" :stroke-width="4"
                      :stroke="getStrokeColor(overall_value.overall_value)" :stroke-dasharray="188.5"
                      :stroke-dashoffset="188.5 - (overall_value.overall_value * 1.885)" transform="rotate(-90 32 32)">
                    </circle>
                  </svg>
                  <div class="score-label">
                    <div class="fw-700 main">{{ overall_value.overall_value }}</div>
                    <!-- <div class="fs-16 fw-500 description">Quality</div> -->
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="line-break"></div>
          <div class="metrics-container">
            <div class="metric-item-2">
              <div class="overview-container">
                <span class="fs-15 fw-700">Overview</span>
                <p class="fs-13 fw-400 mt-8">{{ dataAI.overview }}</p>
                <!-- Tareef has surpassed expectations as a Sales Representative, driving up sales by 30%, fostering invaluable client relationships, and positively collaborating within the team. -->

                <span class="fs-15 fw-700 mt-12">Key Accomplishments</span>
                <ul class="key-accomplishments-list reduced-margin">
                  <li v-for="item in dataAI.keyAccomplishments" :key="item.id" class="mb-3 fs-13 fw-400 mt-8">
                    {{ item }}
                  </li>
                </ul>
                <!-- <span class="fs-13 fw-400 mt-8">
                  • Closed 3 major deals, contributing $1.2M in new revenue
                </span>
                <span class="fs-13 fw-400 mt-8">
                  • Exceeded quarterly targets by 15% consistently
                </span>
                <span class="fs-13 fw-400 mt-8">
                  • Implemented new sales strategy, improving team efficiency by 20%
                </span> -->




                <span class="fs-15 fw-700 mt-8">Performance Journey</span>
                <span class="fs-13 fw-400 mt-8">
                  See how goals and cultural contributions have evolved
                </span>



                <div class="box-container" style="height: 142px;">
                  <div class="box-content">
                    <div class="box-content-item legend" style="justify-content: center;">
                      <div class="box-chart-green ml-12"></div>
                      <div class="fs-11 fw-400 pr-12 pl-6">Exceed</div>
                      <div class="box-chart-light-green"></div>
                      <span class="fs-11 fw-400 pr-12 pl-6">Met</span>
                      <div class="box-chart-yellow"></div>
                      <span class="fs-11 fw-400 pr-12 pl-6">Partially</span>
                      <div class="box-chart-red"></div>
                      <span class="fs-11 fw-400 pr-12 pl-6">Did not meet</span>
                    </div>



                    <div class="box-content-item">
                      <div class="fs-12 fw-400 pr-13 pl-6" style="width: 40px; text-align: end;"></div>
                      <div v-for="i in 12" :key="`${i - 1}goal`" class="flex-mid fs-12 fw-400 mr-6">
                        <div v-if="data_month_year[i - 1].gem" class="flex-mid" style="width: 18px; height: 12px;">
                          <img src="../assets/icons/gem_pink_outline.png" key="gem_pink_outline"
                            style="width: 9px; height: 9px;" />
                        </div>
                        <div v-else style="width: 18px; height: 12px;"></div>
                      </div>
                    </div>

                    <div class="box-content-item">
                      <div class="fs-12 fw-600 pr-13 pl-6" style="width: 40px; text-align: end;">Goals</div>
                      <div v-for="i in 12" :key="`${i - 1}goal`" class="fs-12 fw-400">
                        <div :class="`box-chart-${data_month_year[i - 1] ? data_month_year[i - 1].goal : 'gray'}`"
                          style="width: 18px; height: 16px; margin-right: 6px;"></div>
                      </div>
                    </div>
                    <div class="box-content-item">
                      <div class="fs-12 fw-600 pr-13 pl-6" style="width: 40px;text-align: end;">Values</div>
                      <div v-for="i in 12" :key="`${i - 1}_culture`" class="fs-12 fw-400">
                        <div :class="`box-chart-${data_month_year[i - 1] ? data_month_year[i - 1].culture : 'gray'}`"
                          style="width: 18px; height: 16px; margin-right: 6px;"></div>
                      </div>
                    </div>
                    <div class="box-content-item mt-4 mb-12">
                      <div class="fs-12 fw-400 pr-13 pl-6" style="width: 40px;text-align: end;"></div>
                      <div v-for="i in 12" :key="i" class="fs-12 fw-400">
                        <div style="width: 18px; height: 16px; margin-right: 6px;font-size: 10px;">{{ month[i - 1] }}
                        </div>
                      </div>
                    </div>


                  </div>

                </div>




                <div class="fs-15 fw-700 mt-24">Impact on the Team</div>
                <div class="fs-13 fw-400 mt-8" style="font-style: italic;">
                  How {{ user.firstname }} has been recognized by peers and uplifted others based on our values
                </div>
                <!-- create chart -->
                <div class="box-container" style="height: 273px;">
                  <div class="box-content">
                    <div style="width:321px; height: 243px;">
                      <canvas ref="radarChart" height="243px"></canvas>
                    </div>
                  </div>
                </div>
                <div class="fs-15 fw-700 mt-24">Notable Recognition</div>
                <ul class="key-accomplishments-list reduced-margin">
                  <li v-for="item in dataAI.notableRecognition" :key="item.id" class="mb-3 fs-13 fw-400 mt-8">
                    {{ item }}
                  </li>
                </ul>
                <!-- <div class="fs-13 fw-500 mt-8">• Develop advanced negotiation skills</div>
                <div class="fs-13 fw-500 mt-8">• Enhance product knowledge in new market segments</div>
                <div class="fs-13 fw-500 mt-8">• Improve follow-up process for long-term leads</div> -->
                <div class="fs-15 fw-700 mt-8">Growth Runway</div>
                <ul class="key-accomplishments-list reduced-margin">
                  <li v-for="item in dataAI.growthRunway" :key="item.id" class="mb-3 fs-13 fw-400 mt-8">
                    {{ item }}
                  </li>
                </ul>
                <!-- <div class="fs-13 fw-500 mt-8">• Develop advanced negotiation skills</div>
                <div class="fs-13 fw-500 mt-8">• Enhance product knowledge in new market segments</div>
                <div class="fs-13 fw-500 mt-8">• Improve follow-up process for long-term leads</div> -->
              </div>
            </div>
            <div class="metric-item-2">
              <div class="recognition-container">
                <div v-for="recognition in recognitionGem" :key="recognition.date">

                  <div class="flex-row">
                    <img style="width: 28px; height: 28px;" :src="recognition.image" alt="Recognition Gem">
                    <div class="fs-15 mt-4 fw-600 opa-06 ml-12">{{ recognition.date }}</div>
                  </div>
                  <div class="flex-row">
                    <div class="flex-center" style="width: 28px; justify-content: center;">
                      <div :class="`line-${recognition.color}`"></div>
                    </div>
                    <div class="ml-12" style="width: 100%;">
                      <div class="fs-13 fw-700 mt-18"><span style="text-decoration: underline;">{{ recognition.goal
                          }}</span> Goals</div>
                      <div class="fs-12 mt-8 recognition-detail">{{ recognition.goal_value }}</div>
                      <div class="fs-13 fw-700 mt-15"><span style="text-decoration: underline;">{{ recognition.culture
                          }}</span> Contributed to Values</div>
                      <div class="fs-12 mt-8 mb-20 recognition-detail">{{ recognition.culture_value }}</div>

                    </div>


                  </div>



                </div>
              </div>


            </div>

          </div>
          <div class="line-break"></div>
          <div class="footer-bar">Notes</div>
          <div class="footer-bar-detail">This performance summary is AI-generated from performance boosts given to
            {{ user.firstname }} in the past 12 months. It also gathers insights from peer feedback and recognition
            interactions to provide a 360-degree view of their contributions.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import CryptoJS from "crypto-js";
export default {
  name: 'PerformanceSummaryReport',
  components: {
    loadingEven: require("./even/loading").default,
  },
  data() {
    return {
      // recognitionGem: [
      //   {
      //     image:'https://firebasestorage.googleapis.com/v0/b/people2-admin.appspot.com/o/images%2Femail%2Fsendgem%2Fsq_pink_gem_28.png?alt=media&token=fcbe2996-f5b9-4eec-8b4b-aceed3f39a74',
      //     date: 'August 2024',
      //     goal:'Met',
      //     goal_value:'Wittawat has surpassed expectations as a Sales Representative, driving up sales by 30%, fostering invaluable client relationships, and positively collaborating within the team.',
      //     culture:'Consistently',
      //     culture_value:'Wittawat has embodied our values of #excellence by surpassing his sales targets and exhibiting mastery over product knowledge, and embraced #responsiveness through prompt client communication and agile adaptation to market changes.'
      //   },
      //   {
      //     image:'https://firebasestorage.googleapis.com/v0/b/people2-admin.appspot.com/o/images%2Femail%2Fsendgem%2Fsq_pink_gem_28.png?alt=media&token=fcbe2996-f5b9-4eec-8b4b-aceed3f39a74',
      //     date: 'June 2024',
      //     goal:'Met',
      //     goal_value:'Wittawat has surpassed expectations as a Sales Representative, driving up sales by 30%, fostering invaluable client relationships, and positively collaborating within the team.',
      //     culture:'Consistently',
      //     culture_value:'Wittawat has embodied our values of #excellence by surpassing his sales targets and exhibiting mastery over product knowledge, and embraced #responsiveness through prompt client communication and agile adaptation to market changes.'
      //   },
      //   {
      //     image:'https://firebasestorage.googleapis.com/v0/b/people2-admin.appspot.com/o/images%2Femail%2Fsendgem%2Fsq_gray_gem_28.png?alt=media&token=a37d9466-4c58-410e-8b60-bddeaae43dc0',
      //     date: 'April 2024',
      //     goal:'Met',
      //     goal_value:'Wittawat has surpassed expectations as a Sales Representative, driving up sales by 30%, fostering invaluable client relationships, and positively collaborating within the team.',
      //     culture:'Consistently',
      //     culture_value:'Wittawat has embodied our values of #excellence by surpassing his sales targets and exhibiting mastery over product knowledge, and embraced #responsiveness through prompt client communication and agile adaptation to market changes.'
      //   },
      //   {
      //     image:'https://firebasestorage.googleapis.com/v0/b/people2-admin.appspot.com/o/images%2Femail%2Fsendgem%2Fsq_pink_gem_28.png?alt=media&token=fcbe2996-f5b9-4eec-8b4b-aceed3f39a74',
      //     date: 'February 2024',
      //     goal:'Met',
      //     goal_value:'Wittawat has surpassed expectations as a Sales Representative, driving up sales by 30%, fostering invaluable client relationships, and positively collaborating within the team.',
      //     culture:'Consistently',
      //     culture_value:'Wittawat has embodied our values of #excellence by surpassing his sales targets and exhibiting mastery over product knowledge, and embraced #responsiveness through prompt client communication and agile adaptation to market changes.'
      //   }],
      radarChart: null,
      // loading:true,
      //month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],

    };
  },
  created() {
    let user_email = ''


    let bytes = CryptoJS.AES.decrypt(
      this.$router.currentRoute.params.id,
      "performance-summary-report"
    );
    //console.log('bytes===',bytes)
    let originalText = bytes.toString(CryptoJS.enc.Utf8);
    if (originalText) {
      console.log('originalText===', originalText)
      user_email = originalText
      this.$store.dispatch("performanceSummaryReport/login").then(() => {
        this.$store.dispatch('performanceSummaryReport/getuser', user_email)
        this.$store.dispatch('performanceSummaryReport/getActivityTimeline', user_email)
      });

    }

  },
  computed: {
    recognitionGem() {
      return this.$store.getters['performanceSummaryReport/recognitionGem']
    },
    overall_value() {
      return this.$store.getters['performanceSummaryReport/overall_value']
    },
    month() {
      return this.$store.getters['performanceSummaryReport/month']
    },
    data_month_year() {
      return this.$store.getters['performanceSummaryReport/data_month_year']
    },
    user() {
      return this.$store.getters['performanceSummaryReport/user']
    },
    period() {
      return this.$store.getters['performanceSummaryReport/period']
    },
    radar_Chart() {

      return this.$store.getters['performanceSummaryReport/radar_Chart']
    },
    dataAI() {
      return this.$store.getters['performanceSummaryReport/dataAI']
    },
    loading() {
      return this.$store.getters['performanceSummaryReport/loading']
    }
  },
  watch: {
    radar_Chart() {
      // Only initialize chart when loading is complete
      if (!this.loading && this.radar_Chart) {
        this.$nextTick(() => {
          if (this.radarChart) {
            this.radarChart.destroy();
          }
          this.randerRadarChart();
        });
      }
    },
    loading(newVal) {
      // When loading completes, initialize the chart if we have data
      if (!newVal && this.radar_Chart) {
        this.$nextTick(() => {
          if (this.radarChart) {
            this.radarChart.destroy();
          }
          this.randerRadarChart();
        });
      }
    },
    // Add a new watcher for the user data
    user: {
      handler(newUser) {
        if (newUser && newUser.name) {
          document.title = `${newUser.name} | Performance Summary`;
        }
      },
      immediate: true
    },
  },
  mounted() {

  },
  methods: {
    getStrokeColor(value) {
      if (value >= 80) {
        return "#66BB6A";
      } else if (value >= 51 && value <= 79) {
        return "#FBC02D";
      } else {
        return "#F44336";
      }
    },
    getGreaterValueAlignment(email) {
      if (this.recognition[email]) {
        const given = this.recognition[email].valueAlignmentGiven || 0;
        const received = this.recognition[email].valueAlignmentReceived || 0;
        return Math.max(given, received);
      }
      return 0;
    },
    randerRadarChart() {
      // Add safety check for DOM element
      if (!this.$refs.radarChart) {
        console.warn('Radar chart canvas element not found');
        return;
      }

      var chart = this.$refs.radarChart;
      const radar = this.radar_Chart;

      // Add safety check for data
      if (!radar || !radar.label || !radar.hash) {
        console.warn('Radar chart data not ready');
        return;
      }

      const given = [];
      const received = [];
      const originalValues = {
        given: [],
        received: []
      };

      // Normalize function - scales values to 0-5 range
      const normalizeValue = (value) => {
        const maxPossibleValue = 72 / radar.label.length; // Max recognitions per year divided by number of values
        return Math.min((value / maxPossibleValue) * 5, 5);
      };

      for (const key in radar.label) {
        if (radar.hash[radar.label[key]]) {
          // Store original values
          originalValues.given.push(radar.hash[radar.label[key]].given || 0);
          originalValues.received.push(radar.hash[radar.label[key]].received || 0);

          // Store normalized values for display
          given.push(normalizeValue(radar.hash[radar.label[key]].given || 0));
          received.push(normalizeValue(radar.hash[radar.label[key]].received || 0));
        }
      }

      // Helper function to truncate text while preserving #
      const truncateText = (text, maxLength = 15) => {
        if (text.length <= maxLength) return text;
        // Keep the # and truncate the rest
        return text.substring(0, maxLength - 2) + '...';
      };

      const data = {
        // Transform labels to truncated versions
        labels: radar.label.map(label => truncateText(label)),
        datasets: [{
          label: 'Given',
          data: given,
          originalData: originalValues.given, // Store original values in dataset
          fill: true,
          backgroundColor: 'rgba(98, 0, 255, 0.7)',
          borderColor: 'rgba(98, 0, 255, 0)',
          pointBackgroundColor: 'rgba(98, 0, 255, 1)',
          pointBorderColor: 'rgba(98, 0, 255, 1)',
          pointHoverBackgroundColor: 'rgba(98, 0, 255, 1)',
          pointHoverBorderColor: 'rgba(98, 0, 255, 1)'
        }, {
          label: 'Received',
          data: received,
          originalData: originalValues.received, // Store original values in dataset
          fill: true,
          backgroundColor: 'rgba(69, 214, 209, 0.7)',
          borderColor: 'rgba(54, 162, 235, 0)',
          pointBackgroundColor: 'rgba(54, 162, 235, 1)',
          pointBorderColor: 'rgba(54, 162, 235, 1)',
          pointHoverBackgroundColor: 'rgba(54, 162, 235, 1)',
          pointHoverBorderColor: 'rgba(54, 162, 235, 1)'
        }]
      };

      this.radarChart = new Chart(chart, {
        type: 'radar',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                title: function (context) {
                  // Show full label text in tooltip
                  return radar.label[context[0].dataIndex];
                },
                label: function (context) {
                  const originalValue = context.dataset.originalData[context.dataIndex];
                  return `${context.dataset.label}: ${originalValue}`;
                }
              }
            },
            legend: {
              display: true,
              position: 'bottom',
              align: 'center',
              labels: {
                font: {
                  family: 'Lato',
                  size: 12,
                  weight: 600
                },
                usePointStyle: true,
                pointStyle: 'rectRounded',
                padding: 20
              }
            }
          },
          scales: {
            r: {
              angleLines: {
                display: true,
                color: 'rgba(0, 0, 0, 0.1)'
              },
              ticks: {
                display: false,
                maxTicksLimit: 5
              },
              pointLabels: {
                font: {
                  family: 'Lato',
                  size: 12,
                  weight: 600
                },
                padding: 30,
                centerPointLabels: true,
                callback: function (label) {
                  return truncateText(label, 15);
                }
              },
              min: 0,
              max: 5,
              beginAtZero: true
            }
          },
          layout: {
            padding: {
              top: 20,
              right: 30,
              bottom: 20,
              left: 30  // Increased left padding
            }
          },
          elements: {
            line: {
              borderWidth: 2
            },
            point: {
              radius: 3,
              hoverRadius: 7
            }
          }
        }
      });
    },
    getlink() {
      let user_email = 'tareef_j@dhas.com'
      const ciphertext = CryptoJS.AES.encrypt(user_email, 'performance-summary-report').toString();
      const url = 'https://insights.happily.ai/performance-summary-report/' + encodeURIComponent(ciphertext);
      console.log("===", url)
    }
  },
};
</script>

<style scoped>
.report-container {
  background-color: #FFFFFF;
  width: 100%;
  max-width: 1200px;
  margin: 40px 0%;

  box-shadow: 0 1px 1px rgba(0,0,0,0.03),
              0 2px 2px rgba(0,0,0,0.03),
              0 4px 4px rgba(0,0,0,0.03),
              0 8px 8px rgba(0,0,0,0.03),
              0 16px 16px rgba(0,0,0,0.03);
  border-radius: 8px;
}

.header-bar {
  background-color: #6D27BF;
  height: 16px;
  width: 100%;
  border-radius: 8px 8px 0 0;
}

.content-wrapper {
  padding: 36px 10%;
}

.title {
  font-size: 24px;
  font-weight: 900;
  margin-bottom: 6px;
  margin-top: 0;
}

.subtitle {
  font-size: 15px;
  font-weight: 700;
  color: #6D27BF;
  margin-top: 0;
  margin-bottom: 16px;
}

.metrics-container {
  display: flex;
  flex-direction: column;

}

.metric-item-2 {
  display: flex;
  flex-direction: row;
  margin-top: 8px;
  flex: 50%;
}

.icon-container img {
  width: 60px;
  height: 60px;
  border-radius: 12px;
}

.metrics-grid {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-around;
  margin-top: 12px;
}

.metric-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-items: center;
  /* margin-bottom: 20px; */

}

.metric-item h2 {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
  margin-right: 6px;
}

.icon-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  /* margin-bottom: 16px; */
}

.icon-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 16px;
}

.line-break {
  width: 100%;
  height: 1px;
  background-color: #E0E0E0;
  margin: 16px 0;
}

.overview-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  
}

.box-container {
  display: flex;
  background: #F7F7F7 0% 0% no-repeat padding-box;
  border-radius: 12px;
  height: 142px;
  opacity: 1;
  margin-top: 18px;
  width: 100%;

}

.box-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
}

.box-content-item {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.box-content-item.legend {
  margin-top: 16px;
}

.box-chart-green {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  opacity: 1;
  background-color: #35d077;
}

.box-chart-light-green {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  opacity: 1;
  background-color: #A7E3AA;
}

.box-chart-yellow {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  opacity: 1;
  background-color: #FBC02D;
}

.box-chart-red {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  opacity: 1;
  background-color: #F44336;
}

.box-chart-gray {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  opacity: 1;
  background-color: #D5D5D5;
}

.line-pink {
  top: 502px;
  left: 492px;
  width: 6px;
  height: 148px;
  background: #FFE2E9 0% 0% no-repeat padding-box;
  opacity: 1;
}

.line-gray {
  top: 502px;
  left: 492px;
  width: 6px;
  height: 148px;
  background: #F2F2F2 0% 0% no-repeat padding-box;
  opacity: 1;
}

.recognition-detail {
  width: 100%;
  padding-right: 10%;
}

.relative {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.score-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 20px;
  font-weight: 700;
  color: #000;
  text-align: center;
  z-index: 2;
  line-height: 24px;
  white-space: nowrap;
  width: 100%;
  /* Add this line */
}

.footer-bar {
  font-size: 15px;
  font-weight: 700;
  color: #000;
  /* margin-left: 32px; */
  margin-top: 24px;
}

.footer-bar-detail {
  width: 100%;
  font-size: 13px;
  font-weight: 400;
  color: #000;
  /* margin-left: 32px; */
  margin-top: 4px;
}

/* จอใหญ่ ipad */
@media (min-width: 768px) {
  .report-container {
  width: 100%;
  margin: 40px auto;

  }
  .metrics-container {
    flex-direction: row;
    align-items: flex-start;
  }

  .icon-container {
    margin-right: 20px;
  }

  .icon-container .icon-text {
    margin-left: 16px;
  }

  .metrics-grid {
    flex: 1;
    margin-top: 0px;
  }

  .metric-item {
    flex-wrap: nowrap;
    width: 100%;
  }

  .metric-item h2 {
    margin-right: 0px;
  }

  .overview-container {
    padding-right: 15px;
  }

  .recognition-detail {
    width: 100%;
    padding-right: 10%;
  }

  .relative {
    position: relative;
    width: 100%;
    height: 100%;

  }

  .score-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 20px;
    font-weight: 700;
    color: #000;
    text-align: center;
    z-index: 2;
    line-height: 24px;
    white-space: nowrap;
    width: 100%;
    /* Add this line */
  }

}

@media (min-width: 1024px) {
  .report-container {
  width: 100%;
  margin: 40px auto;

  }
  .content-wrapper {
    padding: 36px 5%;
  }

  .recognition-detail {
    width: 100%;
  }

  .metrics-grid {
    justify-content: space-between;
    margin-top: 0px;

  }

  .overview-container {
    margin-right: 52px;
  }

  .metric-item {
    flex-basis: 30%;
  }

  .relative {
    position: relative;
    width: 100%;
    height: 100%;

  }

  .score-label {
    position: absolute;
    top: 50%;
    /* left: 45%; */
    transform: translate(-50%, -50%);
    font-size: 20px;
    font-weight: 700;
    color: #000;
    text-align: center;
    z-index: 2;
    text-align: center;
    line-height: 24px;
    /* Adjust this if you change the .progress-wrapper dimensions */
    white-space: nowrap;
    /* Prevents the label from wrapping to the next line */
  }
}

.reduced-margin {
  margin-left: 16px;
  /* Adjust this value as needed */
  padding-left: 0;
  margin-top: 8px;
}

/* Add this new style */
.radar-chart-container {
  width: 100%;
  height: 300px;
  /* Reduced height */
  margin: 0 auto;
  padding: 20px;
  /* Added padding */
}

/* Update the chart container size */
.box-content>div {
  width: 360px !important;
  /* Slightly wider to accommodate labels */
  height: 280px !important;
  /* Slightly taller if needed */
}

/* Add these new styles */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
 
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.loading-message {
  margin-top: 24px;
  font-size: 16px;
  font-weight: 700;
  color: #6D27BF;
}

.loading-submessage {
  margin-top: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #666666;
}
</style>
